import React, { useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import './mapPage.scss';
import { JobSiteWithStats } from "../../store/interfaces";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { companyService } from "../../services/apiService";
import { useNavigate } from "react-router-dom";

const containerStyle = {
  width: '100%',
  height: 'Calc(100vh - 300px)',
};

const center = {
  lat: 39.8283,
  lng: -98.5795,
};

const MapPage: React.FC = () => {

  const navigate = useNavigate()

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '', // Replace with your API key
  });

  const [jobSites, setJobSites] = React.useState<JobSiteWithStats[]>([]);
  const [bounds, setBounds] = React.useState<google.maps.LatLngBounds | null>(null);
  const [jobSitesWithCoordinates, setJobSitesWithCoordinates] = React.useState<JobSiteWithStats[]>([]);

  const user = useSelector((state: RootState) => state.user.user);

  useEffect(() => {
    async function fetchData() {
      if (!user || !user.companyId) {
        return;
      }

      try {
        const data = await companyService.getCompanyJobSitesWithStats(user, user.companyId);
        console.log('data', data);

        setJobSites(data);

        // Filter job sites that have valid latitude and longitude coordinates
        const sitesWithCoordinates = data.filter((jobSite) => {
          console.log(`JobSite ${jobSite.name}: lat=${jobSite.latitude} (${typeof jobSite.latitude}), lng=${jobSite.longitude} (${typeof jobSite.longitude})`);

          // Convert to numbers if they're strings
          const lat = typeof jobSite.latitude === 'string' ? parseFloat(jobSite.latitude) : jobSite.latitude;
          const lng = typeof jobSite.longitude === 'string' ? parseFloat(jobSite.longitude) : jobSite.longitude;

          // Check if we have valid coordinates (not null, undefined, or NaN)
          // Note: 0 is a valid coordinate (equator or prime meridian)
          const hasValidCoordinates = lat != null && lng != null &&
            !isNaN(lat) && !isNaN(lng);

          console.log(`JobSite ${jobSite.name}: hasValidCoordinates=${hasValidCoordinates}`);
          return hasValidCoordinates;
        });
        console.log(`Found ${sitesWithCoordinates.length} job sites with coordinates out of ${data.length} total`);
        setJobSitesWithCoordinates(sitesWithCoordinates);
      } catch (error) {
        console.error('Get Company error:', error);
      }
    }

    fetchData();
  }, [user]);

  const mapRef = React.useRef<google.maps.Map | null>(null);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;

    const bounds = new google.maps.LatLngBounds();

    jobSitesWithCoordinates.forEach((jobSite) => {
      // Convert to numbers if they're strings
      const lat = typeof jobSite.latitude === 'string' ? parseFloat(jobSite.latitude) : jobSite.latitude;
      const lng = typeof jobSite.longitude === 'string' ? parseFloat(jobSite.longitude) : jobSite.longitude;

      // Skip if coordinates are invalid
      if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) {
        console.log(`Skipping ${jobSite.name} - invalid coordinates`);
        return;
      }

      console.log(`Creating marker for ${jobSite.name} at lat=${lat}, lng=${lng}`);

      const myLatLng = new google.maps.LatLng(lat, lng);
      bounds.extend(myLatLng);
      const fillColor = jobSite.currentEmployeeCount > 0 ? 'blue' : 'yellow';
      const textColor = jobSite.currentEmployeeCount > 0 ? 'white' : 'black';

      const marker = new google.maps.Marker({
        position: myLatLng,
        map,
        icon: {
          url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
  <polygon points="20 40 40 0 0 0" fill="${fillColor}"/>
    <text x="20" y="10" font-family="Roboto" font-size="8" fill="${textColor}" text-anchor="middle">${jobSite.shortName || jobSite.name.substring(0, 8) || jobSite.jobNumber}</text>
  <text x="20" y="22" font-family="Roboto" font-size="8" fill="${textColor}" text-anchor="middle">${jobSite.currentEmployeeCount}</text>
</svg>`
          )}`,
          scaledSize: new google.maps.Size(40, 40),
        },
        title: jobSite.shortName,
      });

      marker.addListener("click", () => {
        navigate(`/jobSites/${jobSite.id}`)
      });
    });

    // Only set bounds and fit map if we have job sites with coordinates
    if (jobSitesWithCoordinates.length > 0) {
      setBounds(bounds);
      map.fitBounds(bounds);
    }
  }, [jobSitesWithCoordinates, navigate]);

  if (loadError) {
    return <div>Error loading map: {loadError.message}</div>;
  }

  if (!isLoaded) {
    return <div>Loading Map...</div>;
  }

  return jobSitesWithCoordinates.length > 0 ? (
    <div className="jobSite-container">
      <h2>Locations on Map</h2>
      <GoogleMap
        mapContainerStyle={containerStyle}
        onLoad={handleMapLoad}
        center={bounds?.getCenter() || center}
        mapTypeId='satellite'
      />
    </div>
  ) : jobSites.length > 0 ? (
    <div className="jobSite-container">
      <h2>No Job Sites with Coordinates</h2>
      <p>There are {jobSites.length} job sites, but none have latitude and longitude coordinates set.</p>
    </div>
  ) : (
    <div className="jobSite-container">
      <h2>No Job Sites</h2>
      <p>No job sites found for this company.</p>
    </div>
  );
};

export default MapPage;
